import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../Button";
import Chip from "../Chip";
import { getReq, postReq } from "../../utils/request";
import { refreshAiAccess, useAiAccess } from "../../hooks/useAiAccess";
import { MyAiAccess } from "../../types/Ai";
import AiCost from "./AiCost";
import classes from "./assistant.module.scss";

interface AiFeatureView {
    text: string | null;
    madeAt: string | null;
    outdated: boolean;
    outdatedReason: "changed" | "old" | null;
    model: string | null;
}

interface AiResultCardProps {
    title: string;
    /** Button label when there's nothing saved yet. */
    makeLabel: string;
    /** What the button does — the tooltip. */
    makeTitle: string;
    /** Reads the saved result (no AI call). */
    getUrl: string;
    /** Makes a fresh one. */
    postUrl: string;
    postBody?: Record<string, unknown>;
    /** Extra condition on the user's AI access (e.g. a data rule). */
    isAllowed?: (access: MyAiAccess) => boolean;
    /** Shown under the text (e.g. "A draft — review it before use"). */
    note?: string;
}

const timeZone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
};

/**
 * A click-to-run AI result (comments catch-up, threat draft): never made
 * automatically. Shows the saved text with when it was made, marks it
 * Outdated when the source changed (or after 10 days), and remakes it only
 * on a click. Renders nothing when AI isn't on for the user.
 */
const AiResultCard: React.FC<AiResultCardProps> = ({ title, makeLabel, makeTitle, getUrl, postUrl, postBody, isAllowed, note }) => {
    const { access } = useAiAccess();
    const [result, setResult] = useState<AiFeatureView | null>(null);
    const [isMaking, setIsMaking] = useState(false);
    const isOn = !!access?.available && (!isAllowed || isAllowed(access));

    const fetchSaved = useCallback(async () => {
        const response = await getReq<AiFeatureView>(getUrl).catch(() => null);
        setResult(response?.status === 200 ? response.body : null);
    }, [getUrl]);

    useEffect(() => { if (isOn) fetchSaved(); }, [isOn, fetchSaved]);

    if (!isOn) return null;

    const handleMake = async () => {
        setIsMaking(true);
        const response = await postReq<AiFeatureView>(postUrl, { timeZone: timeZone(), ...(postBody ?? {}) }).catch(() => null);
        setIsMaking(false);
        refreshAiAccess();
        if (response?.status === 200 && response.body) setResult(response.body);
        else toast.error((response?.body as any)?.message ?? "The AI couldn't do that right now");
    };

    const hasText = !!result?.text;
    return (
        <div className={classes.resultCard} data-ai-scope>
            <div className={classes.resultHead}>
                <span className={classes.resultTitle}><i className="bi bi-stars" aria-hidden="true" /> {title}</span>
                {hasText && result?.outdated && (
                    <Chip tone="warning" title={result.outdatedReason === "changed" ? "Things changed since it was made — refresh to include them" : "Made more than 10 days ago"}>Outdated</Chip>
                )}
                <Button type="button" variant="ghost" size="xs" icon={hasText ? "arrow-clockwise" : "stars"} loading={isMaking}
                    title={hasText ? "Make it again from the latest data (uses AI)" : makeTitle} onClick={handleMake}>
                    {hasText ? "Refresh" : makeLabel}<AiCost access={access} compact inButton noIcon={!hasText} />
                </Button>
            </div>
            {hasText && (
                <>
                    <div className={classes.resultText}>{result!.text}</div>
                    <div className={classes.muted}>
                        {result!.madeAt ? `Made ${new Date(result!.madeAt).toLocaleString()}` : ""}{note ? ` · ${note}` : ""}
                    </div>
                </>
            )}
        </div>
    );
};

export default AiResultCard;
